import { fromJS, Map } from 'immutable'
import { createReducer } from 'redux-immutablejs'

import { FetchStatus } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { NpPatientBeingBookedFor } from '@lyrahealth-inc/shared-app-logic/src/features/npTesting/types'
import {
  getNpBookableSessionType,
  mapNpSessionsToScheduledSessions,
} from '@lyrahealth-inc/shared-app-logic/src/features/npTesting/utils'
import {
  NpAvailableTimesResponse,
  NpBookableSessionType,
  NpScheduleSessionsResponse,
  NpSelectedAppointment,
  NpSessionsResponse,
} from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { ProviderInfo } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import {
  CLEAR_NP_AVAILABLE_TIMES,
  CLEAR_NP_BOOKING_FLOW,
  CLEAR_NP_SCHEDULED_SESSIONS,
  CLEAR_NP_SELECTED_APPOINTMENTS,
  CLEAR_NP_SELECTED_PROVIDER,
  CLEAR_NP_SESSIONS_LOADING_STATUS,
  GET_NP_AVAILABLE_TIMES_FULFILLED,
  GET_NP_AVAILABLE_TIMES_PENDING,
  GET_NP_AVAILABLE_TIMES_REJECTED,
  GET_NP_SESSIONS_FULFILLED,
  GET_NP_SESSIONS_PENDING,
  GET_NP_SESSIONS_REJECTED,
  SCHEDULE_NP_SESSIONS_FULFILLED,
  SCHEDULE_NP_SESSIONS_PENDING,
  SCHEDULE_NP_SESSIONS_REJECTED,
  SET_NP_CURRENT_SESSION_TYPE,
  SET_NP_PATIENT_BEING_BOOKED_FOR,
  SET_NP_SELECTED_APPOINTMENT,
  SET_NP_SELECTED_PROVIDER,
} from './npTestingActions'

const initialState = fromJS({
  availableTimes: null,
  availableTimesLoadingStatus: null,
  // The appointment being booked and the slots picked so far, keyed by session type. Testing sessions 1 and 2 are
  // picked one after the other and sent in a single schedule-sessions request from the session 2 booking page.
  currentSessionType: null,
  // The member or linked dependent the evaluation is for; every NP request is made with this patient's lyra id
  patientBeingBookedFor: null,
  selectedAppointments: {},
  // The booked sessions and the episodeId later bookings need. Written by the last schedule-sessions request, or by
  // the sessions fetch when the member arrives at the calendar with nothing in progress.
  scheduledSessions: null,
  scheduleSessionsLoadingStatus: null,
  sessionsLoadingStatus: null,
  // In-person provider the member chose on the provider results page; the calendar requests this provider's times
  selectedProvider: null,
})

export default createReducer(initialState, {
  [GET_NP_AVAILABLE_TIMES_PENDING]: ($$state: Map<string, any>) => {
    return $$state.set('availableTimesLoadingStatus', FetchStatus.PENDING)
  },
  [GET_NP_AVAILABLE_TIMES_FULFILLED]: ($$state: Map<string, any>, action: { payload: NpAvailableTimesResponse }) => {
    return $$state.set('availableTimes', fromJS(action.payload)).set('availableTimesLoadingStatus', FetchStatus.SUCCESS)
  },
  [GET_NP_AVAILABLE_TIMES_REJECTED]: ($$state: Map<string, any>, action: { meta?: { aborted?: boolean } }) => {
    // An aborted request (e.g. the page unmounted) is not an error
    if (action.meta?.aborted) {
      return $$state
    }
    return $$state.set('availableTimesLoadingStatus', FetchStatus.ERROR)
  },
  [CLEAR_NP_AVAILABLE_TIMES]: ($$state: Map<string, any>) => {
    // Only the availability is cleared; selections made from it must survive leaving the calendar
    return $$state.set('availableTimes', null).set('availableTimesLoadingStatus', null)
  },
  [SET_NP_CURRENT_SESSION_TYPE]: ($$state: Map<string, any>, action: { payload: NpBookableSessionType }) => {
    return $$state.set('currentSessionType', action.payload)
  },
  [SET_NP_SELECTED_APPOINTMENT]: ($$state: Map<string, any>, action: { payload: NpSelectedAppointment }) => {
    return $$state.setIn(['selectedAppointments', action.payload.sessionType], fromJS(action.payload))
  },
  [CLEAR_NP_SELECTED_APPOINTMENTS]: ($$state: Map<string, any>) => {
    return $$state.set('currentSessionType', null).set('selectedAppointments', fromJS({}))
  },
  [SCHEDULE_NP_SESSIONS_PENDING]: ($$state: Map<string, any>) => {
    return $$state.set('scheduleSessionsLoadingStatus', FetchStatus.PENDING)
  },
  [SCHEDULE_NP_SESSIONS_FULFILLED]: ($$state: Map<string, any>, action: { payload: NpScheduleSessionsResponse }) => {
    return $$state
      .set('scheduledSessions', fromJS(action.payload))
      .set('scheduleSessionsLoadingStatus', FetchStatus.SUCCESS)
  },
  [SCHEDULE_NP_SESSIONS_REJECTED]: ($$state: Map<string, any>) => {
    return $$state.set('scheduleSessionsLoadingStatus', FetchStatus.ERROR)
  },
  [CLEAR_NP_SCHEDULED_SESSIONS]: ($$state: Map<string, any>) => {
    return $$state.set('scheduledSessions', null).set('scheduleSessionsLoadingStatus', null)
  },
  [GET_NP_SESSIONS_PENDING]: ($$state: Map<string, any>) => {
    return $$state.set('sessionsLoadingStatus', FetchStatus.PENDING)
  },
  [GET_NP_SESSIONS_FULFILLED]: ($$state: Map<string, any>, action: { payload: NpSessionsResponse }) => {
    // The member resumes with the next session the episode says to book; null when only the provider can schedule it
    const scheduledSessions = mapNpSessionsToScheduledSessions(action.payload)
    return $$state
      .set('scheduledSessions', scheduledSessions ? fromJS(scheduledSessions) : null)
      .set('currentSessionType', getNpBookableSessionType(action.payload.nextSessionType))
      .set('sessionsLoadingStatus', FetchStatus.SUCCESS)
  },
  [GET_NP_SESSIONS_REJECTED]: ($$state: Map<string, any>, action: { meta?: { aborted?: boolean } }) => {
    // An aborted request (e.g. the page unmounted) is not an error
    if (action.meta?.aborted) {
      return $$state
    }
    return $$state.set('sessionsLoadingStatus', FetchStatus.ERROR)
  },
  [CLEAR_NP_SESSIONS_LOADING_STATUS]: ($$state: Map<string, any>) => {
    // Only the status is cleared; the sessions it loaded are what the rest of the flow runs on
    return $$state.set('sessionsLoadingStatus', null)
  },
  [SET_NP_SELECTED_PROVIDER]: ($$state: Map<string, any>, action: { payload: ProviderInfo }) => {
    return $$state.set('selectedProvider', fromJS(action.payload))
  },
  [CLEAR_NP_SELECTED_PROVIDER]: ($$state: Map<string, any>) => {
    return $$state.set('selectedProvider', null)
  },
  [SET_NP_PATIENT_BEING_BOOKED_FOR]: ($$state: Map<string, any>, action: { payload: NpPatientBeingBookedFor }) => {
    return $$state.set('patientBeingBookedFor', fromJS(action.payload))
  },
  [CLEAR_NP_BOOKING_FLOW]: () => initialState,
})
